import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore, useProgramStore } from '../../stores'
import { ProgramList } from '../../components/program'
import { Button, Input } from '../../components/common'
import { useToast } from '../../components/common/Toast'
import { assignmentService, type AssignmentSummary } from '../../services'
import type { Program } from '../../types/database.types'

type ViewMode = 'all' | 'templates'

export function ProgramsPage() {
  const navigate = useNavigate()
  const { profile } = useAuthStore()
  const [viewMode, setViewMode] = useState<ViewMode>('all')
  const [togglingVisibilityId, setTogglingVisibilityId] = useState<string | null>(null)
  const [assignmentSummaries, setAssignmentSummaries] = useState<Map<string, AssignmentSummary>>(new Map())
  const {
    programs,
    templates,
    isLoading,
    fetchPrograms,
    fetchTemplates,
    deleteProgram,
    duplicateProgram,
    toggleTemplate,
    setVisibility,
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
  } = useProgramStore()
  const toast = useToast()

  useEffect(() => {
    if (profile?.id) {
      fetchPrograms(profile.id)
      fetchTemplates(profile.id)
    }
  }, [profile?.id, fetchPrograms, fetchTemplates, searchQuery, filters])

  // Fetch assignment summaries when programs are loaded
  useEffect(() => {
    if (profile?.id && programs.length > 0) {
      const programIds = programs.map(p => p.id)
      assignmentService
        .getAssignmentSummariesForPrograms(profile.id, programIds)
        .then(setAssignmentSummaries)
        .catch(error => console.error('Failed to fetch assignment summaries:', error))
    }
  }, [profile?.id, programs])

  const handleDelete = async (program: Program) => {
    if (!confirm(`Delete "${program.title}"? This action cannot be undone.`)) {
      return
    }
    try {
      await deleteProgram(program.id)
      toast.success('Program deleted')
    } catch (error) {
      toast.error('Failed to delete program')
    }
  }

  const handleDuplicate = async (program: Program) => {
    if (!profile?.id) return
    try {
      const newProgram = await duplicateProgram(program.id, profile.id)
      if (newProgram) {
        toast.success('Program duplicated')
        navigate(`/dashboard/programs/${newProgram.id}`)
      }
    } catch (error) {
      toast.error('Failed to duplicate program')
    }
  }

  const handleToggleTemplate = async (program: Program) => {
    try {
      const newTemplateStatus = !program.is_template
      await toggleTemplate(program.id, newTemplateStatus)
      toast.success(newTemplateStatus ? 'Saved as template' : 'Removed from templates')
    } catch (error) {
      toast.error('Failed to update template status')
    }
  }

  const handleUseTemplate = async (program: Program) => {
    if (!profile?.id) return
    try {
      const newProgram = await duplicateProgram(program.id, profile.id, `${program.title} - New`)
      if (newProgram) {
        toast.success('Program created from template')
        navigate(`/dashboard/programs/${newProgram.id}`)
      }
    } catch (error) {
      toast.error('Failed to create from template')
    }
  }

  const handleToggleVisibility = async (program: Program) => {
    setTogglingVisibilityId(program.id)
    try {
      const newVisibility = program.visibility === 'public' ? 'private' : 'public'
      await setVisibility(program.id, newVisibility)
      toast.success(`Program is now ${newVisibility}`)
    } catch (error) {
      toast.error('Failed to update visibility')
    } finally {
      setTogglingVisibilityId(null)
    }
  }

  // Filter programs based on view mode
  const displayPrograms = viewMode === 'templates'
    ? programs.filter(p => p.is_template)
    : programs

  const canCreatePrograms = profile?.subscription_tier !== 'free'

  if (!canCreatePrograms) {
    return (
      <div className="max-w-2xl mx-auto text-center py-12">
        <svg
          className="w-16 h-16 text-gray-400 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Program Creation Locked
        </h1>
        <p className="text-gray-600 mb-6">
          Upgrade to Basic or higher to create and manage exercise programs.
        </p>
        <Link to="/dashboard/subscription">
          <Button>Upgrade Subscription</Button>
        </Link>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Programs</h1>
          <p className="mt-1 text-gray-600">
            Create and manage exercise programs for your clients
          </p>
        </div>
        <Link to="/dashboard/programs/new">
          <Button>Create Program</Button>
        </Link>
      </div>

      {/* View Mode Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setViewMode('all')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              viewMode === 'all'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            All Programs
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
              {programs.length}
            </span>
          </button>
          <button
            onClick={() => setViewMode('templates')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              viewMode === 'templates'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Templates
            <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-600">
              {templates.length}
            </span>
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search programs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          value={filters.visibility || ''}
          onChange={(e) =>
            setFilters({
              visibility:
                (e.target.value as Program['visibility']) || undefined,
            })
          }
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All visibility</option>
          <option value="private">Private</option>
          <option value="organization">Organization</option>
          <option value="public">Public</option>
        </select>
      </div>

      {/* Program list */}
      <ProgramList
        programs={displayPrograms}
        assignmentSummaries={assignmentSummaries}
        isLoading={isLoading}
        onDeleteProgram={handleDelete}
        onDuplicateProgram={handleDuplicate}
        onToggleTemplate={handleToggleTemplate}
        onUseTemplate={viewMode === 'templates' ? handleUseTemplate : undefined}
        onToggleVisibility={handleToggleVisibility}
        togglingVisibilityId={togglingVisibilityId}
        emptyMessage={
          viewMode === 'templates'
            ? 'No templates yet. Mark a program as template to reuse it.'
            : searchQuery
              ? 'No programs match your search'
              : 'No programs yet. Create your first program!'
        }
      />
    </div>
  )
}
